import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Upload, Plus, Trash2, ArrowUp, ArrowDown, ImageOff } from 'lucide-react';
import { uploadBuilderImage, resolveUrl } from './builderApi';
import {
  ICON_CHOICES, BG_LABELS, SPACING_LABELS, WIDTH_LABELS, ALIGN_LABELS, BLOCK_LABELS,
} from './blockDefs';

const L = ({ children }) => <Label className="text-gray-300 text-[11px] uppercase tracking-widest">{children}</Label>;
const Field = ({ label, children }) => <div className="space-y-1">{label && <L>{label}</L>}{children}</div>;
const inputCls = 'bg-slate-800 border-slate-700 text-white text-sm';

const Choice = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => {
      const val = o.key ?? o;
      const lbl = o.label ?? o;
      const active = value === val;
      return (
        <button key={val} type="button" data-testid={testid ? `${testid}-${val}` : undefined}
          onClick={() => onChange(val)}
          className={`px-3 py-1.5 rounded text-xs border transition-all ${active ? 'border-red-500 bg-red-600/20 text-white' : 'border-slate-700 bg-slate-800 text-gray-400 hover:border-slate-600'}`}>
          {lbl}
        </button>
      );
    })}
  </div>
);

const ImagePicker = ({ url, onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = () => ref.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image too large (max 8 MB)'); e.target.value = ''; return; }
    setBusy(true);
    try {
      const u = await uploadBuilderImage(file);
      onChange(u);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className="space-y-2">
      <div className="rounded-md border border-slate-700 bg-slate-950 overflow-hidden flex items-center justify-center h-32">
        {url ? <img src={resolveUrl(url)} alt="" className="max-h-32 object-contain" /> : <ImageOff className="w-8 h-8 text-slate-600" />}
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid={testid ? `${testid}-input` : undefined} />
      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={pick} disabled={busy} data-testid={testid ? `${testid}-btn` : undefined} className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white text-xs flex-1">
          <Upload className="w-3.5 h-3.5 mr-1.5" /> {busy ? 'Uploading…' : (url ? 'Replace' : 'Upload')}
        </Button>
        {url && <Button type="button" size="sm" variant="outline" onClick={() => onChange('')} className="border-slate-700 text-gray-300 text-xs"><Trash2 className="w-3.5 h-3.5" /></Button>}
      </div>
    </div>
  );
};

const IconChoice = ({ value, onChange }) => (
  <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="bg-slate-800 border border-slate-700 text-white rounded px-2 py-1.5 text-xs w-full">
    {ICON_CHOICES.map((k) => <option key={k} value={k}>{k}</option>)}
  </select>
);

// Editable list of {icon,title,description,(features)} items (services/cards/values).
const ItemList = ({ items, onChange, withFeatures, withIcon = true }) => {
  const set = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= items.length) return;
    const next = [...items]; [next[i], next[j]] = [next[j], next[i]]; onChange(next);
  };
  const add = () => onChange([...items, { id: Math.random().toString(36).slice(2, 10), icon: 'shield', title: 'New item', description: '' }]);
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={it.id || i} className="rounded-md border border-slate-700 bg-slate-950/50 p-3 space-y-2" data-testid={`pp-item-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-gray-500">Item {i + 1}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-white disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 text-gray-400 hover:text-white disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => del(i)} data-testid={`pp-item-del-${i}`} className="p-1 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          {withIcon && <div className="flex items-center gap-2"><span className="text-[10px] text-gray-500 w-8">Icon</span><IconChoice value={it.icon} onChange={(v) => set(i, { icon: v })} /></div>}
          <Input value={it.title || ''} onChange={(e) => set(i, { title: e.target.value })} placeholder="Title" className={inputCls} />
          <Textarea value={it.description || ''} onChange={(e) => set(i, { description: e.target.value })} placeholder="Description" rows={2} className={inputCls} />
          {withFeatures && (
            <Textarea value={(it.features || []).join('\n')} onChange={(e) => set(i, { features: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })} placeholder="One feature per line" rows={2} className={`${inputCls} font-mono text-xs`} />
          )}
        </div>
      ))}
      <Button type="button" size="sm" onClick={add} data-testid="pp-item-add" className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white text-xs w-full"><Plus className="w-3.5 h-3.5 mr-1" /> Add item</Button>
    </div>
  );
};

export default function PropertiesPanel({ selection, section, block, onBlock, onBlockSettings, onSection }) {
  if (!selection) {
    return <div className="text-gray-500 text-sm p-4">Select a section or block to edit its content and settings.</div>;
  }

  // ---- Section settings ----
  if (selection.kind === 'section') {
    const s = section?.settings || {};
    const isSelfContained = (section?.blocks || []).some((b) => ['hero', 'services', 'gallery_preview', 'pricing', 'image_text', 'values', 'contact'].includes(b.type));
    return (
      <div className="space-y-4 p-4" data-testid="pp-section">
        <h4 className="text-white font-semibold text-sm">Section settings</h4>
        {isSelfContained ? (
          <p className="text-[11px] text-gray-500">This is a pre-styled section. Background & spacing are controlled by its design. Select the block inside to edit its content.</p>
        ) : (
          <>
            <Field label="Background"><Choice options={BG_LABELS} value={s.bg || 'none'} onChange={(v) => onSection({ bg: v })} testid="pp-sec-bg" /></Field>
            <Field label="Top & bottom spacing"><Choice options={SPACING_LABELS} value={s.spacing || 'normal'} onChange={(v) => onSection({ spacing: v })} testid="pp-sec-spacing" /></Field>
            <Field label="Content width"><Choice options={WIDTH_LABELS} value={s.width || 'normal'} onChange={(v) => onSection({ width: v })} testid="pp-sec-width" /></Field>
            <Field label="Text alignment"><Choice options={ALIGN_LABELS} value={s.align || 'center'} onChange={(v) => onSection({ align: v })} testid="pp-sec-align" /></Field>
            <Field label="Columns"><Choice options={[{ key: 1, label: 'One' }, { key: 2, label: 'Two' }, { key: 3, label: 'Three' }]} value={s.columns || 1} onChange={(v) => onSection({ columns: v })} testid="pp-sec-cols" /></Field>
          </>
        )}
      </div>
    );
  }

  // ---- Block content ----
  if (!block) return <div className="text-gray-500 text-sm p-4">Block not found.</div>;
  const c = block.content || {};
  const bs = block.settings || {};
  const set = (patch) => onBlock(patch);

  return (
    <div className="space-y-4 p-4" data-testid="pp-block">
      <h4 className="text-white font-semibold text-sm">{BLOCK_LABELS[block.type] || block.type} settings</h4>

      {block.type === 'hero' && (<>
        <Field label="Business name"><Input value={c.name || ''} onChange={(e) => set({ name: e.target.value })} className={inputCls} data-testid="pp-hero-name" /></Field>
        <Field label="Headline"><Input value={c.headline || ''} onChange={(e) => set({ headline: e.target.value })} className={inputCls} data-testid="pp-hero-headline" /></Field>
        <Field label="Sub-headline"><Textarea value={c.subline || ''} onChange={(e) => set({ subline: e.target.value })} rows={3} className={inputCls} data-testid="pp-hero-subline" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Primary button"><Input value={c.primary_cta_text || ''} onChange={(e) => set({ primary_cta_text: e.target.value })} className={inputCls} /></Field>
          <Field label="Primary link"><Input value={c.primary_cta_link || ''} onChange={(e) => set({ primary_cta_link: e.target.value })} className={`${inputCls} font-mono text-xs`} /></Field>
          <Field label="Secondary button"><Input value={c.secondary_cta_text || ''} onChange={(e) => set({ secondary_cta_text: e.target.value })} className={inputCls} /></Field>
          <Field label="Secondary link"><Input value={c.secondary_cta_link || ''} onChange={(e) => set({ secondary_cta_link: e.target.value })} className={`${inputCls} font-mono text-xs`} /></Field>
        </div>
        <Field label="Tagline"><Input value={c.tagline || ''} onChange={(e) => set({ tagline: e.target.value })} className={inputCls} /></Field>
        <Field label="Logo image"><ImagePicker url={c.logo_url} onChange={(u) => set({ logo_url: u })} testid="pp-hero-logo" /></Field>
      </>)}

      {block.type === 'services' && (<>
        <Field label="Heading"><Input value={c.heading || ''} onChange={(e) => set({ heading: e.target.value })} className={inputCls} data-testid="pp-services-heading" /></Field>
        <Field label="Sub-heading"><Input value={c.subheading || ''} onChange={(e) => set({ subheading: e.target.value })} className={inputCls} /></Field>
        <Field label="Service cards"><ItemList items={c.items || []} onChange={(items) => set({ items })} withFeatures /></Field>
      </>)}

      {block.type === 'values' && (<>
        <Field label="Heading"><Input value={c.heading || ''} onChange={(e) => set({ heading: e.target.value })} className={inputCls} data-testid="pp-values-heading" /></Field>
        <Field label="Columns"><Choice options={[{ key: 1, label: 'One' }, { key: 2, label: 'Two' }, { key: 3, label: 'Three' }]} value={bs.columns || 2} onChange={(v) => onBlockSettings({ columns: v })} /></Field>
        <Field label="Value cards"><ItemList items={c.items || []} onChange={(items) => set({ items })} /></Field>
      </>)}

      {block.type === 'cards' && (<>
        <Field label="Heading"><Input value={c.heading || ''} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
        <Field label="Columns"><Choice options={[{ key: 1, label: 'One' }, { key: 2, label: 'Two' }, { key: 3, label: 'Three' }]} value={bs.columns || 2} onChange={(v) => onBlockSettings({ columns: v })} /></Field>
        <Field label="Cards"><ItemList items={c.items || []} onChange={(items) => set({ items })} /></Field>
      </>)}

      {(block.type === 'gallery_preview' || block.type === 'pricing') && (<>
        <Field label="Heading"><Input value={c.heading || ''} onChange={(e) => set({ heading: e.target.value })} className={inputCls} data-testid="pp-heading" /></Field>
        <Field label="Sub-heading"><Input value={c.subheading || ''} onChange={(e) => set({ subheading: e.target.value })} className={inputCls} /></Field>
        <p className="text-[11px] text-gray-500">{block.type === 'pricing' ? 'Price rows are managed in the Services tab.' : 'Gallery photos are managed in the Gallery tab.'}</p>
      </>)}

      {block.type === 'contact' && <p className="text-[11px] text-gray-500">The contact form and details come from your Business Info. Move or hide this section as needed.</p>}

      {block.type === 'image_text' && (<>
        <Field label="Heading"><Input value={c.heading || ''} onChange={(e) => set({ heading: e.target.value })} className={inputCls} data-testid="pp-it-heading" /></Field>
        <Field label="Sub-heading"><Input value={c.subheading || ''} onChange={(e) => set({ subheading: e.target.value })} className={inputCls} /></Field>
        <Field label="Image"><ImagePicker url={c.image_url} onChange={(u) => set({ image_url: u })} testid="pp-it-image" /></Field>
        <Field label="Image caption"><Input value={c.image_caption || ''} onChange={(e) => set({ image_caption: e.target.value })} className={inputCls} /></Field>
        <Field label="Image position"><Choice options={[{ key: 'left', label: 'Left' }, { key: 'right', label: 'Right' }]} value={c.image_pos || 'left'} onChange={(v) => set({ image_pos: v })} /></Field>
        <Field label="Paragraphs (blank line between)"><Textarea value={(c.paragraphs || []).join('\n\n')} onChange={(e) => set({ paragraphs: e.target.value.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean) })} rows={8} className={inputCls} /></Field>
      </>)}

      {block.type === 'heading' && (<>
        <Field label="Text"><Input value={c.text || ''} onChange={(e) => set({ text: e.target.value })} className={inputCls} data-testid="pp-heading-text" /></Field>
        <Field label="Size"><Choice options={[{ key: 'small', label: 'S' }, { key: 'normal', label: 'M' }, { key: 'large', label: 'L' }, { key: 'xlarge', label: 'XL' }]} value={c.size || 'large'} onChange={(v) => set({ size: v })} /></Field>
        <Field label="Alignment"><Choice options={ALIGN_LABELS} value={c.align || 'center'} onChange={(v) => set({ align: v })} /></Field>
        <Field label="Color"><input type="color" value={c.color || '#ffffff'} onChange={(e) => set({ color: e.target.value })} className="w-full h-9 rounded bg-slate-800 border border-slate-700" /></Field>
      </>)}

      {block.type === 'text' && (<>
        <Field label="Text"><Textarea value={c.text || ''} onChange={(e) => set({ text: e.target.value })} rows={6} className={inputCls} data-testid="pp-text-text" /></Field>
        <Field label="Size"><Choice options={[{ key: 'small', label: 'S' }, { key: 'normal', label: 'M' }, { key: 'large', label: 'L' }]} value={c.size || 'normal'} onChange={(v) => set({ size: v })} /></Field>
        <Field label="Alignment"><Choice options={ALIGN_LABELS} value={c.align || 'left'} onChange={(v) => set({ align: v })} /></Field>
        <Field label="Color"><input type="color" value={c.color || '#e2e8f0'} onChange={(e) => set({ color: e.target.value })} className="w-full h-9 rounded bg-slate-800 border border-slate-700" /></Field>
      </>)}

      {block.type === 'image' && (<>
        <Field label="Image"><ImagePicker url={c.url} onChange={(u) => set({ url: u })} testid="pp-image" /></Field>
        <Field label="Alt text"><Input value={c.alt || ''} onChange={(e) => set({ alt: e.target.value })} className={inputCls} /></Field>
        <Field label={`Width: ${c.width || 100}%`}><input type="range" min="20" max="100" value={c.width || 100} onChange={(e) => set({ width: Number(e.target.value) })} className="w-full" /></Field>
        <Field label="Alignment"><Choice options={ALIGN_LABELS} value={c.align || 'center'} onChange={(v) => set({ align: v })} /></Field>
        <Field label="Link (optional)"><Input value={c.link || ''} onChange={(e) => set({ link: e.target.value })} className={`${inputCls} font-mono text-xs`} placeholder="/#contact" /></Field>
      </>)}

      {block.type === 'button' && (<>
        <Field label="Button text"><Input value={c.text || ''} onChange={(e) => set({ text: e.target.value })} className={inputCls} data-testid="pp-button-text" /></Field>
        <Field label="Link"><Input value={c.link || ''} onChange={(e) => set({ link: e.target.value })} className={`${inputCls} font-mono text-xs`} /></Field>
        <Field label="Style"><Choice options={[{ key: 'solid', label: 'Solid' }, { key: 'outline', label: 'Outline' }]} value={c.style || 'solid'} onChange={(v) => set({ style: v })} /></Field>
        <Field label="Size"><Choice options={[{ key: 'small', label: 'S' }, { key: 'normal', label: 'M' }, { key: 'large', label: 'L' }]} value={c.size || 'normal'} onChange={(v) => set({ size: v })} /></Field>
        <Field label="Alignment"><Choice options={ALIGN_LABELS} value={c.align || 'center'} onChange={(v) => set({ align: v })} /></Field>
      </>)}

      {block.type === 'cta' && (<>
        <Field label="Heading"><Input value={c.heading || ''} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
        <Field label="Text"><Textarea value={c.text || ''} onChange={(e) => set({ text: e.target.value })} rows={2} className={inputCls} /></Field>
        <Field label="Button text"><Input value={c.button_text || ''} onChange={(e) => set({ button_text: e.target.value })} className={inputCls} /></Field>
        <Field label="Button link"><Input value={c.button_link || ''} onChange={(e) => set({ button_link: e.target.value })} className={`${inputCls} font-mono text-xs`} /></Field>
      </>)}

      {block.type === 'gallery' && (<>
        <Field label="Heading"><Input value={c.heading || ''} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
        <Field label="Images"><GalleryItemList items={c.items || []} onChange={(items) => set({ items })} /></Field>
      </>)}

      {block.type === 'spacer' && (
        <Field label="Height"><Choice options={[{ key: 'small', label: 'Small' }, { key: 'normal', label: 'Normal' }, { key: 'large', label: 'Large' }]} value={c.size || 'normal'} onChange={(v) => set({ size: v })} /></Field>
      )}
      {block.type === 'divider' && <p className="text-[11px] text-gray-500">A horizontal line. Nothing to configure.</p>}
    </div>
  );
}

const GalleryItemList = ({ items, onChange }) => {
  const set = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-md border border-slate-700 bg-slate-950/50 p-3 space-y-2">
          <ImagePicker url={it.url} onChange={(u) => set(i, { url: u })} />
          <Input value={it.caption || ''} onChange={(e) => set(i, { caption: e.target.value })} placeholder="Caption (optional)" className={inputCls} />
          <Button type="button" size="sm" variant="outline" onClick={() => del(i)} className="border-slate-700 text-red-400 text-xs w-full"><Trash2 className="w-3.5 h-3.5 mr-1" /> Remove</Button>
        </div>
      ))}
      <Button type="button" size="sm" onClick={() => onChange([...items, { url: '', caption: '' }])} className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white text-xs w-full"><Plus className="w-3.5 h-3.5 mr-1" /> Add image</Button>
    </div>
  );
};
